import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { FormControl, FormGroup, NgControl } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';
import { SgFormControlBase } from '../../shared';
import { SgSelectOption } from '../../models/select-option.model';
import { DATE_PRESET_OPTIONS, DatePreset, DateRangeValue } from './resolve-date-range';

/** Reactive shape backing the Material `mat-date-range-input`. Values are Luxon `DateTime` (the app-wide adapter). */
interface DateRangeGroup {
  start: FormControl<DateTime | null>;
  end: FormControl<DateTime | null>;
}

/**
 * A composite date-range filter control built on the house Material calendar
 * (`mat-date-range-input` / `mat-date-range-picker`) — NOT native `<input type="date">`.
 *
 * Its ControlValueAccessor value (`DateRangeValue`) is the user's *selection state*
 * (a preset, plus picked calendar dates for `custom`), never a frozen `{from,to}`.
 * Callers must call the exported pure `resolveDateRange(control.value)` at QUERY TIME
 * to turn that selection into concrete `fromDate`/`toDate` ISO bounds — this keeps
 * rolling presets ("past hour", …) relative to the actual fetch.
 *
 * - `presets = true`  → a preset dropdown (`sg-dropdown-2`); picking `Custom` reveals
 *   the Material range picker.
 * - `presets = false` → just the Material range picker (its value is resolved like
 *   `custom`: local start-of-day / end-of-day normalization).
 *
 * The Material datepicker modules + the app's custom Luxon `DateAdapter` are already
 * provided app-wide by `SgMaterialAllModule` (imported by `SgCoreModule`), so no
 * per-component adapter wiring is needed. That adapter constructs dates in UTC
 * (`DateTime.utc(...)` in its `createDate`, not via a `useUtc` option), so picked values
 * arrive as UTC `DateTime`s and are stored as calendar-only `YYYY-MM-DD` strings.
 */
@Component({
  selector: 'sg-date-range',
  templateUrl: './sg-date-range.component.html',
  styleUrls: ['./sg-date-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgDateRangeComponent extends SgFormControlBase<DateRangeValue> implements OnInit, OnDestroy {
  static ngAcceptInputType_presets: BooleanInput;

  /** When true, show the preset dropdown (Custom reveals the range picker); when false, show only the range picker. */
  @Input()
  get presets(): boolean {
    return this._presets;
  }
  set presets(value: BooleanInput) {
    this._presets = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _presets = false;

  /** Preset applied on init / when the CVA value has no preset. */
  @Input() defaultPreset: DatePreset = 'today';

  /** Size input mirroring `sg-dropdown-2` (governs the preset dropdown for house consistency). */
  @Input() size: 'default' | 'small' = 'default';

  // Composite filter control: default to a suppressed subscript so it lines up flush in the
  // flex-end filter bars (overrides SgFormControlBase's 'fixed' default). Governs the preset
  // dropdown; the range field's subscript is suppressed via scss when this is 'none'.
  @Input() override subscriptSizing: 'fixed' | 'dynamic' | 'none' = 'none';

  @Output() readonly valueChange = new EventEmitter<DateRangeValue>();

  readonly presetOptions: SgSelectOption<DatePreset>[] = DATE_PRESET_OPTIONS;

  readonly presetControl = new FormControl<DatePreset>('today', { nonNullable: true });
  readonly rangeGroup = new FormGroup<DateRangeGroup>({
    start: new FormControl<DateTime | null>(null),
    end: new FormControl<DateTime | null>(null),
  });

  private destroy$ = new Subject<void>();

  constructor(@Optional() @Self() override ngControl: NgControl | null) {
    super(ngControl);
  }

  get empty(): boolean {
    const value = this._value;

    return !value || (!value.preset && !value.fromDate && !value.toDate);
  }

  /** The Material range picker is shown when there are no presets, or the `custom` preset is picked. */
  get showCustomRange(): boolean {
    return !this.presets || this.presetControl.value === 'custom';
  }

  ngOnInit(): void {
    // Seed the preset only when no CVA value has been written yet — otherwise a writeValue that
    // ran before ngOnInit (ordering is not guaranteed for [formControl] hosts) would be clobbered.
    if (!this._value) {
      this.presetControl.setValue(this.defaultPreset, { emitEvent: false });
    }

    // Recompose + emit on every user selection. writeValue mutates these controls with
    // { emitEvent: false }, so model->view syncs never loop back through here.
    this.presetControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.emitValue());
    this.rangeGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.emitValue());
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  writeValue(value: DateRangeValue | null): void {
    this._value = value ?? null;

    this.presetControl.setValue(value?.preset ?? this.defaultPreset, { emitEvent: false });
    this.rangeGroup.setValue(
      {
        // Stored dates are calendar-only (YYYY-MM-DD); parse in UTC to match the app's
        // custom Luxon adapter (which builds dates via DateTime.utc) so the calendar
        // highlights the picked day exactly.
        start: value?.fromDate ? DateTime.fromISO(value.fromDate, { zone: 'utc' }) : null,
        end: value?.toDate ? DateTime.fromISO(value.toDate, { zone: 'utc' }) : null,
      },
      { emitEvent: false }
    );

    this.cdr.markForCheck();
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);

    if (isDisabled) {
      this.presetControl.disable({ emitEvent: false });
      this.rangeGroup.disable({ emitEvent: false });
    } else {
      this.presetControl.enable({ emitEvent: false });
      this.rangeGroup.enable({ emitEvent: false });
    }
  }

  private emitValue(): void {
    const value = this.buildValue();
    this._value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private buildValue(): DateRangeValue {
    if (!this.presets) {
      return this.readRangeDates();
    }

    const preset = this.presetControl.value;

    // Only `custom` carries explicit from/to; a rolling preset is resolved from "now" at
    // query time, so its stored value stays a bare `{ preset }`.
    return preset === 'custom' ? { preset, ...this.readRangeDates() } : { preset };
  }

  private readRangeDates(): { fromDate?: string; toDate?: string } {
    const { start, end } = this.rangeGroup.value;
    const result: { fromDate?: string; toDate?: string } = {};

    const fromDate = start?.toISODate();
    const toDate = end?.toISODate();
    if (fromDate) result.fromDate = fromDate;
    if (toDate) result.toDate = toDate;

    return result;
  }
}
